import Phaser from "phaser";

interface BeepOpts {
  start: number;
  end: number;
  duration: number;
  type: OscillatorType;
  peak: number;
}

interface NoiseOpts {
  duration: number;
  peak: number;
  highpass?: number;
  lowpass?: number;
}

/**
 * Programmatic SFX via the Web Audio API. No audio file loads, no
 * license attribution overhead — every sound is a short oscillator +
 * envelope generated in-engine.
 *
 * Why programmatic for v0.3: a license-clean sample bank (PRD §8 /
 * TODO §8) hasn't landed yet, and CC-licensed sample files complicate
 * the §8b license-gate. Web Audio gives us a usable game-feel layer
 * for free while the sample bank is sourced. When the sample bank
 * arrives in v0.4, each method here can be swapped to play a loaded
 * sample without changing the public call sites.
 *
 * Eight events wired in v0.3: jump, double-jump, attack, geo collect,
 * inventory add, skill unlock, HP loss, HP gain. All are short (under
 * 350 ms) and quiet (master gain 0.35) so they layer cleanly without
 * stealing attention from gameplay.
 */
export class AudioService {
  private ctx?: AudioContext;
  private master?: GainNode;
  private muted = false;

  constructor(scene: Phaser.Scene) {
    const mgr = scene.sound;
    if (mgr instanceof Phaser.Sound.WebAudioSoundManager) {
      this.ctx = mgr.context;
    }
    if (!this.ctx) {
      try {
        const w = window as unknown as {
          AudioContext?: typeof AudioContext;
          webkitAudioContext?: typeof AudioContext;
        };
        const C = w.AudioContext ?? w.webkitAudioContext;
        if (C) this.ctx = new C();
      } catch (e) {
        console.warn("[audio] no AudioContext available; SFX disabled", e);
      }
    }
    if (this.ctx) {
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.35;
      this.master.connect(this.ctx.destination);
    }
  }

  mute(m: boolean): void {
    this.muted = m;
  }

  /** Resume the context — browsers require a user gesture to start audio. */
  resume(): void {
    if (this.ctx?.state === "suspended") void this.ctx.resume();
  }

  jump(): void {
    this.beep({ start: 420, end: 720, duration: 0.09, type: "triangle", peak: 0.09 });
  }

  doubleJump(): void {
    this.beep({ start: 520, end: 880, duration: 0.08, type: "triangle", peak: 0.09 });
    this.beepAfter(0.06, { start: 740, end: 1100, duration: 0.09, type: "triangle", peak: 0.10 });
  }

  /** Sword swing — a short noise sweep that reads as a swish through air. */
  attack(): void {
    this.noiseBurst({ duration: 0.06, peak: 0.14, highpass: 700 });
    this.noiseBurstAfter(0.04, { duration: 0.08, peak: 0.16, highpass: 2200 });
    this.noiseBurstAfter(0.10, { duration: 0.05, peak: 0.10, highpass: 3600 });
  }

  /** Gegner defeated — quick low thud + bright crack so kills read distinctly. */
  gegnerDefeated(): void {
    this.beep({ start: 260, end: 80, duration: 0.12, type: "square", peak: 0.20 });
    this.noiseBurst({ duration: 0.08, peak: 0.18, highpass: 1200 });
    this.beepAfter(0.05, { start: 520, end: 220, duration: 0.10, type: "triangle", peak: 0.16 });
  }

  /** Portal traversal — ascending wormhole whoosh with a shimmer tail. */
  portalEnter(): void {
    this.beep({ start: 220, end: 1100, duration: 0.32, type: "sine", peak: 0.18 });
    this.beepAfter(0.05, { start: 330, end: 1320, duration: 0.32, type: "triangle", peak: 0.14 });
    this.noiseBurstAfter(0.10, { duration: 0.22, peak: 0.08, highpass: 3000 });
  }

  /** Entering a swamp band — wet "shlup" + secondary slosh, as if wading
   *  a boot into mud. Lowpassed noise gives the muddy/wet character;
   *  the brief upward chirp between steps reads as suction release. */
  swampEnter(): void {
    this.beep({ start: 180, end: 70, duration: 0.14, type: "sine", peak: 0.16 });
    this.noiseBurst({ duration: 0.16, peak: 0.18, lowpass: 900 });
    this.beepAfter(0.09, { start: 90, end: 220, duration: 0.05, type: "triangle", peak: 0.10 });
    this.beepAfter(0.22, { start: 150, end: 80, duration: 0.12, type: "sine", peak: 0.12 });
    this.noiseBurstAfter(0.22, { duration: 0.14, peak: 0.14, lowpass: 700 });
  }

  geoCollect(): void {
    this.beep({ start: 880, end: 1320, duration: 0.10, type: "triangle", peak: 0.18 });
  }

  inventoryAdd(): void {
    this.beep({ start: 660, end: 660, duration: 0.06, type: "triangle", peak: 0.16 });
    this.beepAfter(0.05, { start: 880, end: 880, duration: 0.06, type: "triangle", peak: 0.16 });
    this.beepAfter(0.10, { start: 1100, end: 1100, duration: 0.10, type: "triangle", peak: 0.18 });
  }

  skillUnlock(): void {
    // C-E-G major arpeggio + sustained G — the "you got something" beat.
    this.beepAfter(0.0, { start: 523, end: 523, duration: 0.1, type: "triangle", peak: 0.16 });
    this.beepAfter(0.1, { start: 659, end: 659, duration: 0.1, type: "triangle", peak: 0.16 });
    this.beepAfter(0.2, { start: 784, end: 784, duration: 0.2, type: "triangle", peak: 0.22 });
  }

  hpLoss(): void {
    this.beep({ start: 220, end: 110, duration: 0.3, type: "sawtooth", peak: 0.2 });
    this.noiseBurst({ duration: 0.1, peak: 0.1, highpass: 200 });
  }

  hpGain(): void {
    this.beep({ start: 440, end: 880, duration: 0.18, type: "sine", peak: 0.18 });
    this.beepAfter(0.05, { start: 660, end: 1320, duration: 0.18, type: "sine", peak: 0.18 });
  }

  /** Market trade accepted — short ascending chord (cha-ching feel). */
  shopTrade(): void {
    this.beepAfter(0.0, { start: 587, end: 587, duration: 0.08, type: "triangle", peak: 0.18 });
    this.beepAfter(0.07, { start: 740, end: 740, duration: 0.08, type: "triangle", peak: 0.20 });
    this.beepAfter(0.14, { start: 988, end: 988, duration: 0.16, type: "triangle", peak: 0.24 });
  }

  /** Market trade rejected — quick descending two-tone blip. */
  shopReject(): void {
    this.beep({ start: 320, end: 220, duration: 0.10, type: "square", peak: 0.18 });
    this.beepAfter(0.10, { start: 180, end: 130, duration: 0.10, type: "square", peak: 0.18 });
  }

  /**
   * Mechanical ratchet/cog click for scrolling the offer buffer. Layers a
   * low square-wave thud with a short highpassed noise burst to read as
   * "a ratchet cog catching" instead of a generic UI tick.
   */
  shopScroll(): void {
    this.beep({ start: 180, end: 110, duration: 0.05, type: "square", peak: 0.18 });
    this.noiseBurst({ duration: 0.04, peak: 0.16, highpass: 1800 });
    this.beepAfter(0.04, { start: 90, end: 70, duration: 0.04, type: "square", peak: 0.12 });
  }

  /** New trade offer arrived — bell-like two-tone chime with a soft tail. */
  shopOfferArrived(): void {
    this.beep({ start: 988, end: 988, duration: 0.10, type: "sine", peak: 0.20 });
    this.beepAfter(0.06, { start: 1318, end: 1318, duration: 0.16, type: "sine", peak: 0.22 });
    this.beepAfter(0.18, { start: 1568, end: 1568, duration: 0.22, type: "triangle", peak: 0.16 });
    // Quiet shimmer on the tail so it reads as a notification, not a power-up.
    this.noiseBurstAfter(0.04, { duration: 0.18, peak: 0.05, highpass: 4000 });
  }

  private beep(opts: BeepOpts): void {
    if (this.muted || !this.ctx || !this.master) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.start, ctx.currentTime);
    if (opts.end !== opts.start) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(20, opts.end),
        ctx.currentTime + opts.duration,
      );
    }
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(opts.peak, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + opts.duration);
    osc.connect(gain);
    gain.connect(this.master);
    osc.start();
    osc.stop(ctx.currentTime + opts.duration);
  }

  private beepAfter(delaySec: number, opts: BeepOpts): void {
    setTimeout(() => this.beep(opts), delaySec * 1000);
  }

  private noiseBurstAfter(delaySec: number, opts: NoiseOpts): void {
    setTimeout(() => this.noiseBurst(opts), delaySec * 1000);
  }

  private noiseBurst(opts: NoiseOpts): void {
    if (this.muted || !this.ctx || !this.master) return;
    const ctx = this.ctx;
    const buffer = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * opts.duration)), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.7;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(opts.peak, ctx.currentTime + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + opts.duration);
    let node: AudioNode = src;
    if (opts.highpass) {
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = opts.highpass;
      node.connect(hp);
      node = hp;
    }
    if (opts.lowpass) {
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = opts.lowpass;
      node.connect(lp);
      node = lp;
    }
    node.connect(gain);
    gain.connect(this.master);
    src.start();
  }
}
